import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@assets/icons/search.svg';
import EmptyStateIcon from '@assets/icons/table/results-empty-state.svg';
import { BaseButton, BaseCheckbox, BaseTag } from '@components';
import { BaseInput } from '@components';
import { MULTI_SELECT_TABLE_VIEW_VALUE_TAG_CLASS } from '@constants/table.ts';
import { TEST_IDS } from '@constants/testIds.ts';
import { Trans, useLingui } from '@lingui/react/macro';
import {
    SHARED_RANKS_CUSTOM_RENDER_CANCEL,
    SHARED_RANKS_CUSTOM_RENDER_CLEAR_ALL,
    SHARED_RANKS_CUSTOM_RENDER_DONE,
    SHARED_RANKS_CUSTOM_RENDER_INFO_TEXT,
    SHARED_RANKS_CUSTOM_RENDER_NO_SUBTYPES_FOUND,
    SHARED_RANKS_CUSTOM_RENDER_SELECT_ALL,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/ranks/ui/constants.ts';
import { LabelWrapper } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/ranks/ui/styled.tsx';
import type { TransformedSubItemForMatricesRanks } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/ranks/utils.ts';
import {
    getNextCheckedSubItems,
    getNextSelectedGroupsAndTypes,
    getNormalizedSubItems,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/ranks/utils.ts';
import {
    ButtonsWrapper,
    CheckboxLabelWrapper,
    CustomCategoryFilterWrapper as Wrapper,
    EmptyStateText,
    EmptyStateWrapper,
    LabelItemsSection,
    LevelLabelLight,
    LevelLabelRegular,
    SelectedCountSectionRanks,
    SubItemCategoryLabel,
    SubItemsSection,
    SubitemWrapper,
    ValueGroupHeader,
    ValuesGroupSection,
    WarningText,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/styled.tsx';
import type {
    CustomRenderProps,
    TransformedSubItemForMatrices,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/types.ts';
import {
    getNextSelectedCategories,
    getNextSelectedTypes,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/utils.ts';
import { translateItem } from '@utils/translations.ts';

/*
<CustomCategorySelectorRanks
    levelOneLabel={'DEPARTMENT'}
    levelTwoLabel={'LEVEL'}
    levelOneItems={[
        { value: 'd', label: 'Deck' },
        { value: 'e', label: 'Engine' },
        { value: 'c', label: 'Catering' },
    ]}
    levelTwoItems={[
        { value: 'm', label: 'Mgmt' },
        { value: 'o', label: 'Ops' },
        { value: 's', label: 'Sup' },
    ]}
    searchText={'Search subtypes...'}
    subItems={[
        { value: 'mas', label: 'MAS', type: 'd', category: 'm' },
        { value: 'c/o', label: 'C/O', type: 'd', category: 'm' },
        { value: '2/o', label: '2/O', type: 'd', category: 'o' },
        { value: '3/o', label: '3/O', type: 'd', category: 'o' },
        { value: 'c/e', label: 'C/E', type: 'e', category: 'm' },
        { value: '2/e', label: '2/E', type: 'e', category: 'm' },
        { value: 'c/ck', label: 'C/CK', type: 'c', category: 'o' },
        { value: 'stw', label: 'STW', type: 'c', category: 's' },
        { value: 'mess', label: 'MESS', type: 'c', category: 's' },
    ]}
    selectedSubItems={[]}
/>
 */

export const RanksCustomRender: React.FC<CustomRenderProps> = ({
    levelOneLabel,
    levelTwoLabel,
    levelOneItems,
    levelTwoItems,
    subItems,
    searchText,
    selectedSubItems,
    deselectOnly,
    initiallySelectedSubItems,
    onCancel,
    onDone,
}) => {
    const { t } = useLingui();

    const CLICK_TO_FILTER = t`Click to filter`;
    const NO_SUBTYPES_FOUND = translateItem(SHARED_RANKS_CUSTOM_RENDER_NO_SUBTYPES_FOUND);
    const INFO_TEXT = translateItem(SHARED_RANKS_CUSTOM_RENDER_INFO_TEXT);

    const [searchValue, setSearchValue] = useState('');
    const [selectedTypeValues, setSelectedTypeValues] = useState<string[]>([]);
    const [selectedCategoryValues, setSelectedCategoryValues] = useState<string[]>([]);
    // Tracks explicitly applied filters separately from checkbox-derived selection state.
    const [filteredTypeValues, setFilteredTypeValues] = useState<string[]>([]);
    const [filteredCategoryValues, setFilteredCategoryValues] = useState<string[]>([]);
    const [checkedSubItemValues, setCheckedSubItemValues] = useState<Set<string>>(() => new Set());

    const typedSubItems = subItems as TransformedSubItemForMatricesRanks[];

    const nextCheckedSubItemValues = useMemo(() => new Set(selectedSubItems.map(String)), [selectedSubItems]);

    const allSubItems = useMemo(
        () =>
            getNormalizedSubItems(typedSubItems, levelOneItems, levelTwoItems).sort(
                (a, b) => a.itemOrder - b.itemOrder,
            ),
        [typedSubItems, levelOneItems, levelTwoItems],
    );

    const initiallySelectedSet = useMemo(
        () => new Set(initiallySelectedSubItems.map(String)),
        [initiallySelectedSubItems],
    );

    const allowedSubItems = useMemo(
        () =>
            deselectOnly
                ? allSubItems.filter((subItem) => initiallySelectedSet.has(String(subItem.value)))
                : allSubItems,
        [deselectOnly, allSubItems, initiallySelectedSet],
    );

    const isCleared =
        checkedSubItemValues.size === 0 && selectedTypeValues.length === 0 && selectedCategoryValues.length === 0;

    const hasAllowedGroupItems = (groupValue: string) => allowedSubItems.some((subItem) => subItem.type === groupValue);

    const hasAllowedTypeItems = (typeValue: string) =>
        allowedSubItems.some((subItem) => subItem.category === typeValue);

    const isAllowedSubItem = (value: string) => !deselectOnly || initiallySelectedSet.has(String(value));

    const initiallyUsedDepartments = useMemo(
        () =>
            new Set(
                levelOneItems
                    .map((item) => item.value)
                    .filter((departmentValue) => {
                        const departmentSubItems = allowedSubItems.filter(
                            (subItem) => subItem.type === departmentValue,
                        );

                        if (departmentSubItems.length === 0) return false;

                        return departmentSubItems.every((subItem) => initiallySelectedSet.has(String(subItem.value)));
                    }),
            ),
        [levelOneItems, allowedSubItems, initiallySelectedSet],
    );

    const initiallyUsedLevels = useMemo(
        () =>
            new Set(
                levelTwoItems
                    .map((item) => item.value)
                    .filter((levelValue) => {
                        const levelSubItems = allowedSubItems.filter((subItem) => subItem.category === levelValue);

                        if (levelSubItems.length === 0) return false;

                        return levelSubItems.every((subItem) => initiallySelectedSet.has(String(subItem.value)));
                    }),
            ),
        [levelTwoItems, allowedSubItems, initiallySelectedSet],
    );

    const visibleSubItems = useMemo(() => {
        const searchQuery = searchValue.trim().toLowerCase();

        return allowedSubItems.filter((subItem: TransformedSubItemForMatrices) => {
            const matchesDepartment = filteredTypeValues.length === 0 || filteredTypeValues.includes(subItem.type);

            const matchesLevel =
                filteredCategoryValues.length === 0 || filteredCategoryValues.includes(subItem.category);

            const matchesSearch = !searchQuery || subItem.label.toLowerCase().includes(searchQuery);

            return matchesDepartment && matchesLevel && matchesSearch;
        });
    }, [allowedSubItems, searchValue, filteredTypeValues, filteredCategoryValues]);

    const handleLabelClick = (isLevelOneLabel: boolean, labelValue: string) => {
        if (deselectOnly) {
            const hasAllowedItems = isLevelOneLabel
                ? hasAllowedGroupItems(labelValue)
                : hasAllowedTypeItems(labelValue);

            if (!hasAllowedItems) return;
        }

        const { groups, types } = getNextSelectedGroupsAndTypes(
            selectedTypeValues,
            selectedCategoryValues,
            isLevelOneLabel,
            labelValue,
        );

        setSelectedTypeValues(groups);
        setSelectedCategoryValues(types);
        setFilteredTypeValues(groups);
        setFilteredCategoryValues(types);

        if (groups.length === 0 && types.length === 0) {
            setCheckedSubItemValues(new Set());
            return;
        }

        const nextCheckedSubItems = getNextCheckedSubItems(
            groups,
            types,
            levelOneItems,
            levelTwoItems,
            allowedSubItems,
        );

        setCheckedSubItemValues(nextCheckedSubItems);
    };

    const handleSelectAll = () => {
        const nextCheckedSubItemValues = new Set(checkedSubItemValues);

        visibleSubItems
            .filter((subItem) => isAllowedSubItem(subItem.value))
            .forEach((subItem) => nextCheckedSubItemValues.add(String(subItem.value)));

        const nextSelectedTypes = getNextSelectedTypes(levelOneItems, allowedSubItems, nextCheckedSubItemValues);

        const nextSelectedCategories = getNextSelectedCategories(
            levelTwoItems,
            allowedSubItems,
            nextCheckedSubItemValues,
        );

        setCheckedSubItemValues(nextCheckedSubItemValues);
        setSelectedTypeValues(nextSelectedTypes);
        setSelectedCategoryValues(nextSelectedCategories);
    };

    const handleClearAll = () => {
        setCheckedSubItemValues(new Set());
        setSelectedTypeValues([]);
        setSelectedCategoryValues([]);
        setFilteredTypeValues([]);
        setFilteredCategoryValues([]);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    const handleCheckboxChange = (subItemValue: string, nextChecked: boolean) => {
        setCheckedSubItemValues((prev) => {
            const nextCheckedSubItemValues = new Set(prev);

            if (nextChecked) {
                nextCheckedSubItemValues.add(String(subItemValue));
            } else {
                nextCheckedSubItemValues.delete(String(subItemValue));
            }

            const nextSelectedTypes = getNextSelectedTypes(levelOneItems, allowedSubItems, nextCheckedSubItemValues);

            const nextSelectedCategories = getNextSelectedCategories(
                levelTwoItems,
                allowedSubItems,
                nextCheckedSubItemValues,
            );

            setSelectedTypeValues(nextSelectedTypes);
            setSelectedCategoryValues(nextSelectedCategories);

            return nextCheckedSubItemValues;
        });
    };

    const getLevelTwoLabel = (value: string) => {
        const match = levelTwoItems.find((item) => item.value === value);

        return match?.label ?? '';
    };

    const handleCancel = () => {
        setCheckedSubItemValues(new Set(selectedSubItems.map(String)));
        onCancel();
    };

    const handleDone = () => {
        onDone([...checkedSubItemValues]);
    };

    useEffect(() => {
        setCheckedSubItemValues(nextCheckedSubItemValues);
        setSelectedTypeValues(getNextSelectedTypes(levelOneItems, allowedSubItems, nextCheckedSubItemValues));
        setSelectedCategoryValues(getNextSelectedCategories(levelTwoItems, allowedSubItems, nextCheckedSubItemValues));

        setFilteredTypeValues([]);
        setFilteredCategoryValues([]);
    }, [nextCheckedSubItemValues, levelOneItems, levelTwoItems, allowedSubItems]);

    return (
        <Wrapper $isFixedWidth>
            <LabelItemsSection>
                <div>
                    <LevelLabelRegular>{t`${levelOneLabel}`}</LevelLabelRegular>
                    <LevelLabelLight> {`(${CLICK_TO_FILTER})`}</LevelLabelLight>
                </div>
                <div>
                    {levelOneItems.map((item) => (
                        <LabelWrapper
                            data-cy={TEST_IDS.TENANT_CLIENT.ranksLevelOne(item.value)}
                            $isSelected={selectedTypeValues.includes(item.value)}
                            $wasUsedBefore={!isCleared && initiallyUsedDepartments.has(item.value)}
                            onClick={() => handleLabelClick(true, item.value)}
                            key={item.value}
                        >
                            <BaseTag className={MULTI_SELECT_TABLE_VIEW_VALUE_TAG_CLASS}>{item.label}</BaseTag>
                        </LabelWrapper>
                    ))}
                </div>
            </LabelItemsSection>

            <LabelItemsSection>
                <div>
                    <LevelLabelRegular>{t`${levelTwoLabel}`}</LevelLabelRegular>
                    <LevelLabelLight> {`(${CLICK_TO_FILTER})`}</LevelLabelLight>
                </div>
                <div>
                    {levelTwoItems.map((item) => (
                        <LabelWrapper
                            data-cy={TEST_IDS.TENANT_CLIENT.ranksLevelTwo(item.value)}
                            $isSelected={selectedCategoryValues.includes(item.value)}
                            $wasUsedBefore={!isCleared && initiallyUsedLevels.has(item.value)}
                            onClick={() => handleLabelClick(false, item.value)}
                            key={item.value}
                        >
                            <BaseTag className={MULTI_SELECT_TABLE_VIEW_VALUE_TAG_CLASS}>{item.label}</BaseTag>
                        </LabelWrapper>
                    ))}
                </div>
            </LabelItemsSection>

            <ButtonsWrapper>
                <BaseButton $customType="outline" onClick={handleSelectAll}>
                    {translateItem(SHARED_RANKS_CUSTOM_RENDER_SELECT_ALL)}
                </BaseButton>
                <BaseButton $customType="outline" onClick={handleClearAll}>
                    {translateItem(SHARED_RANKS_CUSTOM_RENDER_CLEAR_ALL)}
                </BaseButton>
            </ButtonsWrapper>

            <BaseInput
                allowClear
                value={searchValue}
                placeholder={searchText}
                prefixIcon={<SearchIcon />}
                onChange={(event) => handleSearch(event.target.value)}
            />

            {visibleSubItems.length > 0 ? (
                <SubItemsSection>
                    {levelOneItems
                        .map((dept) => {
                            const itemsInGroup = visibleSubItems.filter((si) => si.type === dept.value);
                            if (itemsInGroup.length === 0) return null;

                            return (
                                <ValuesGroupSection key={dept.value}>
                                    <ValueGroupHeader>{dept.label}</ValueGroupHeader>

                                    {itemsInGroup.map((subItem) => (
                                        <SubitemWrapper
                                            key={subItem.value}
                                            data-cy={TEST_IDS.TENANT_CLIENT.ranksSubItem(subItem.value)}
                                        >
                                            <CheckboxLabelWrapper>
                                                <BaseCheckbox
                                                    onChange={(e) =>
                                                        handleCheckboxChange(subItem.value, e.target.checked)
                                                    }
                                                    checked={checkedSubItemValues.has(String(subItem.value))}
                                                    disabled={!isAllowedSubItem(subItem.value)}
                                                >
                                                    {subItem.label}
                                                </BaseCheckbox>
                                            </CheckboxLabelWrapper>

                                            <SubItemCategoryLabel>
                                                {getLevelTwoLabel(subItem.category)}
                                            </SubItemCategoryLabel>
                                        </SubitemWrapper>
                                    ))}
                                </ValuesGroupSection>
                            );
                        })
                        .filter(Boolean)}
                </SubItemsSection>
            ) : (
                <EmptyStateWrapper>
                    <EmptyStateIcon />
                    <EmptyStateText>{NO_SUBTYPES_FOUND}</EmptyStateText>
                </EmptyStateWrapper>
            )}

            <SelectedCountSectionRanks>
                <WarningText>{INFO_TEXT}</WarningText>
                <Trans>
                    {checkedSubItemValues.size} of {allowedSubItems.length} selected
                </Trans>
            </SelectedCountSectionRanks>

            <ButtonsWrapper>
                <BaseButton $customType="outline" onClick={handleCancel}>
                    {translateItem(SHARED_RANKS_CUSTOM_RENDER_CANCEL)}
                </BaseButton>
                <BaseButton onClick={handleDone}>{translateItem(SHARED_RANKS_CUSTOM_RENDER_DONE)}</BaseButton>
            </ButtonsWrapper>
        </Wrapper>
    );
};
